import { useState } from "react";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";

function TemplateLibrary() {
  const [activeTab, setActiveTab] = useState("UTILITY");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sampleTemplates = {
    UTILITY: [
      {
        name: "order_update",
        language: "en",
        category: "UTILITY",
        body: "Hello {{1}}, your order {{2}} has been shipped and will arrive soon.",
        footer: "Thank you for shopping with us!",
        samples: ["John", "#12345"], // ✅ samples for {{1}} and {{2}}
      },
    ],
    MARKETING: [
      {
        name: "promo_offer",
        language: "en",
        category: "MARKETING",
        body: "Hi {{1}}, enjoy an exclusive {{2}}% discount on your next purchase! Use code {{3}} at checkout.",
        footer: "Limited time offer!",
        samples: ["Jane", "20", "SAVE20"], // ✅ 3 placeholders
      },
    ],
    AUTHENTICATION: [
      {
        name: "otp_code",
        language: "en",
        category: "AUTHENTICATION",
        body: "{{1}} is your verification code.",
        samples: ["654321"], // ✅ 1 placeholder
      },
    ],
  };

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    if (!selectedTemplate) return;

    setLoading(true);
    setError(null);
    try {
      // Extract variable numbers from body
      const regex = /\{\{(\d+)\}\}/g;
      let match;
      const variables = [];
      while ((match = regex.exec(selectedTemplate.body)) !== null) {
        variables.push(parseInt(match[1]));
      }

      const uniqueVariables = [...new Set(variables)].sort();

      // Generate sample values for each variable
      const sampleValues = uniqueVariables.map((num) => `Sample${num}`);

      const templateData = {
        name: `${selectedTemplate.name}_${Date.now()}`, // unique name
        language: selectedTemplate.language,
        category: selectedTemplate.category,
        status: "PENDING",
        source: "library",
        whatsappTemplateId: `lib_${Date.now()}`,
        components: [
          {
            type: "BODY",
            text: selectedTemplate.body,
            ...(selectedTemplate.samples?.length > 0
              ? { example: { body_text: [selectedTemplate.samples] } }
              : {}),
          },
          ...(selectedTemplate.footer
            ? [{ type: "FOOTER", text: selectedTemplate.footer }]
            : []),
        ],
      };

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/templates`,
        templateData,
        {
          headers: {
            "Content-Type": "application/json",
            "x-email": localStorage.getItem("tenantEmail"),
          },
        }
      );

      alert("Template created successfully!");
    } catch (err) {
      console.error("Error creating template:", err);
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to create template. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  return (
    <div
      className="container my-4"
      style={{
        maxHeight: "calc(100vh - 64px)",
        overflowY: "auto",
        margin: "0 auto",
      }}
    >
      <h2 className="text-center mb-4">Template Library</h2>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Tabs */}
      <ul className="nav nav-tabs justify-content-center mb-4">
        {["UTILITY", "MARKETING", "AUTHENTICATION"].map((tab) => (
          <li className="nav-item" key={tab}>
            <button
              className={`nav-link ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          </li>
        ))}
      </ul>

      {/* Templates Grid */}
      <div className="row">
        {sampleTemplates[activeTab].map((template, index) => (
          <div className="col-md-6 mb-3" key={index}>
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <h5 className="card-title">{template.name}</h5>
                <p className="card-text">{template.body}</p>
                {template.footer && (
                  <p className="text-muted small">{template.footer}</p>
                )}
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => handleTemplateClick(template)}
                >
                  Use This Template
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Confirmation Modal */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Template Creation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to create{" "}
          <strong>{selectedTemplate?.name}</strong> as a new template?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirm(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button variant="success" onClick={handleConfirm} disabled={loading}>
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Creating...
              </>
            ) : (
              "Yes, Create"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default TemplateLibrary;
